/**
 * Library-free helpers for turning a raw language-detection result into a publishable Seavien
 * language tag. Kept separate from the detector adapter (`PublishLanguageDetection.ts`) so unit
 * tests can exercise the mapping/threshold logic without transitively importing the detection
 * library (which keeps `ts-jest` happy regardless of the library's module format).
 */

/**
 * A detection sample shorter than this (after whitespace collapse) is not trustworthy, so we leave
 * the existing default in place rather than guessing from a few characters.
 */
export const MIN_DETECTION_LENGTH = 24;

/**
 * Minimum detector confidence (0..1) before we let a detected language replace the default.
 * Calibrated against tinyld: real prose in a supported language scores ~0.7-1.0, while gibberish
 * and proper-noun-heavy mis-guesses (e.g. an English title misread as French) score well under 0.1,
 * so 0.5 keeps confident detections and rejects the noise. Below this we keep the current selection.
 */
export const MIN_DETECTION_CONFIDENCE = 0.5;

/**
 * Maps a detector's language code to our publishable tag. Most ISO-639-1 codes already match our
 * tags 1:1; the entries here are the ones that differ. Tagalog (`tl`) is published as Filipino
 * (`fil`); Norwegian variants normalise to Bokmål (`nb`).
 */
const DETECTOR_CODE_TO_TAG: Record<string, string> = {
    tl: "fil",
    fil: "fil",
    no: "nb",
    nb: "nb",
    nn: "nb",
};

/**
 * Normalises a raw detector code (e.g. `"en"`, `"pt-BR"`, `"TL"`) to a candidate Seavien tag.
 * Returns the lowercased base code mapped through the alias table; callers must still confirm the
 * result is in the publishable/allowed set.
 */
export function detectorCodeToTag(rawCode: string | null | undefined): string | null {
    if (!rawCode) return null;
    const base = rawCode.toLowerCase().split(/[-_]/)[0];
    if (!base) return null;
    return DETECTOR_CODE_TO_TAG[base] ?? base;
}

/** Collapses whitespace and trims; used to gate on a meaningful amount of text. */
export function normalizeDetectionSample(text: string | null | undefined): string {
    return (text ?? "").replace(/\s+/g, " ").trim();
}

/** True when the sample is long enough to attempt detection. */
export function isDetectableSample(text: string | null | undefined): boolean {
    return normalizeDetectionSample(text).length >= MIN_DETECTION_LENGTH;
}

/**
 * Primary language subtag of a locale tag (the part before the first `-` or `_`), lowercased.
 * `"es-MX"` → `"es"`, `"EN"` → `"en"`, null/blank → `""`.
 */
export function primaryLanguageSubtag(localeTag: string | null | undefined): string {
    const trimmed = (localeTag ?? "").trim();
    if (!trimmed) return "";
    return trimmed.split(/[-_]/)[0].toLowerCase();
}

/**
 * Whether two locale tags share the same primary language subtag — the client mirror of the
 * backend `LocaleMatching.samePrimaryLanguage` used by the publish-time group language gate.
 * If either tag is null/blank (unknown/undetected language) this returns `true`: a missing
 * language must never hide a group or block a selection. There is no universal-English pass —
 * English into a Spanish group is a mismatch just like the reverse.
 */
export function samePrimaryLanguage(tagA: string | null | undefined, tagB: string | null | undefined): boolean {
    const a = primaryLanguageSubtag(tagA);
    const b = primaryLanguageSubtag(tagB);
    if (!a || !b) return true;
    return a === b;
}

/**
 * Resolves a detector code + confidence to a publishable tag, or `null` when it should be ignored
 * (too low confidence, unknown code, or not in the allowed set).
 */
export function resolveDetectedTag(
    rawCode: string | null | undefined,
    confidence: number,
    allowedTags: readonly string[],
): string | null {
    if (confidence < MIN_DETECTION_CONFIDENCE) return null;
    const tag = detectorCodeToTag(rawCode);
    if (!tag) return null;
    return allowedTags.includes(tag) ? tag : null;
}
